// @ts-check
import {
    nextArrowCustom,
    prevArrowCustom,
    defaultOptions,
} from "../global/slick-options";
import { get, getAll, getById } from "../utilities/querySelector";
import { slugify } from "../utilities/slugify";

const modal = get("[data-modal]");
const modalContent = get("[data-modal-content-target]", modal);
const modalSlider = jQuery('[data-slider="modal-slider"]');

/**
 * Set Modal Slider Config if it exists
 */
const options = modalSlider.length
    ? {
        dots: false,
        infinite: true,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
    }
    : {};

/**
 * Add Modal open events to all elements with data-open-modal attribute
 */
document.body.addEventListener("click", handleBodyClick);
function handleBodyClick(event) {
    if (event.target.matches("[data-open-modal]")) {
        event.preventDefault();
        openModal(event.target);
    }

    if (event.target.matches("[data-close-modal]")) {
        closeModal();
    }
}

/**
 * Add Modal close event when area outside of modal is clicked
 */
modal.addEventListener("click", handleModalClick);
function handleModalClick(event) {
    if (event.target === this) {
        closeModal();
    }
}

/**
 * Open modal and dynamically change content based on attributes of the trigger
 * @param {HTMLElement} modalTrigger
 */
function openModal(modalTrigger) {
    modal?.setAttribute("data-modal-loading", "loading");

    const {
        modalTarget: modalId,
        modalTheme,
        modalType,
    } = modalTrigger.dataset;

    const modalContentHtml = get(
        `[data-modal-content-id="${modalId}"]`
    )?.innerHTML;


    const newModalContent = document.createElement('div');
    newModalContent.innerHTML = modalContentHtml;
    modalContent.replaceChildren(...newModalContent.childNodes);

    if (modalType === "gallery") {
        reinitModalSlider(modalContent, modalTrigger);
    }

    if (modalType === "booking") {

        const { modalPostId: postId, modalPostName: postName } = modalTrigger.dataset;
        if (postId) {
            const postIdField = get("input[name='post-id']", modalContent);
            if (postIdField) postIdField.value = postId;
        }
        if (postName) {
            const postNameField = get("input[name='post-name']", modalContent);
            if (postNameField) postNameField.value = postName;

            const submitButton = get("button[type='submit']", modalContent);
            if (submitButton) submitButton.id = slugify(postName);
        }

        reinitForm(modalContent);
    }

    handleModalTheme(modal, modalTheme);

    const anchorLinks = getAll("[data-anchor-link", modalContent);
    if (anchorLinks) {
        anchorLinks.forEach((anchorLink) => {
            anchorLink.addEventListener("click", handleModalClick);
        });
    }

    //If modal already open, ignore the status attribute
    if (modal?.getAttribute("data-modal") === "open") {
        setModalAttributes(modal, "open", modalId, modalType);
    } else {
        setModalAttributes(modal, "", modalId, modalType);
        setTimeout(() => {
            modal?.setAttribute("data-modal", "open");
        }, 500);
    }

    modal?.setAttribute("data-modal-loading", "");
}

/**
 * Close modal and remove content
 */
function closeModal() {
    modal?.setAttribute("data-modal-loading", "loading");
    modal?.setAttribute("data-modal", "close");

    setTimeout(() => {
        setModalAttributes(modal, "", "", "");
        modalContent.innerHTML = "";
    }, 1000);

    modal?.setAttribute("data-modal-loading", "");
}

/**
 * Set Modal Attributes from parameters
 * @param {HTMLElement} modal
 * @param {String} modalStatus
 * @param {String} modalId
 * @param {String} modalType
 */
function setModalAttributes(modal, modalStatus, modalId, modalType) {
    modal?.setAttribute("data-modal", modalStatus);

    setTimeout(() => {
        modal?.setAttribute("data-modal-id", modalId);
        modal?.setAttribute("data-modal-type", modalType);
    }, 1000);
}

/**
 * Handle Modal Theme class update
 * @param {HTMLElement} modal
 * @param {String} modalTheme
 */
function handleModalTheme(modal, modalTheme) {
    if (modalTheme) {
        modal?.classList.add(modalTheme);
    } else {
        const classList = modal?.className.split(/\s+/);
        classList.forEach((className) => {
            if (className.includes("theme--")) {
                modal?.classList.remove(className);
            }
        });
    }
}

/**
 * Reinitialize Slick Slider
 * @param {HTMLElement} modalContent
 * @param {HTMLElement} modalTrigger
 */
function reinitModalSlider(modalContent, modalTrigger) {
    let modalContentSlider = get('[data-slider="modal-slider"]', modalContent);

    if (modalContentSlider) {
        const modalContentSliderIndex = modalTrigger.nextElementSibling.dataset.slide;
        modalContentSlider = jQuery(modalContentSlider);

        modalContentSlider.slick({
            ...options,
            prevArrow: prevArrowCustom(modalContentSlider),
            nextArrow: nextArrowCustom(modalContentSlider),
        });

        modalContentSlider.slick("setPosition");
        modalContentSlider.slick('slickGoTo', modalContentSliderIndex);
    }
}

/**
 * Reinitialize Contact Form 7 Form
 * @param {HTMLElement} modalContent
 */
function reinitForm(modalContent) {
    const form = get("form.wpcf7-form", modalContent);

    if (form && window.wpcf7) {
        window.wpcf7.init( form );
    }

    reinitDatePicker(modalContent);
}

/**
 * Reinitialize Datepicker using function from date-time-picker-for-contact-form-7 plugin
 * @param {HTMLElement} modalContent
*/
function reinitDatePicker(modalContent) {
    const fromDateField = get("input.walcf7-datepicker[name='from-date']", modalContent);
    const toDateField = get("input.walcf7-datepicker[name='to-date']", modalContent);
    const tomorrow = new Date();
    const currentYear = new Date().getFullYear();
    const endYear = currentYear + 5;

    tomorrow.setDate(tomorrow.getDate() + 1);

    const datePickerOptions = {
        format: "d/m/Y",
        timepicker: false,
        //minDate should be one day after today
        minDate: tomorrow,
        yearStart: currentYear,
        yearEnd: endYear,
        scrollMonth: false,
        scrollTime: false,
        scrollInput: false,
    }

    if (fromDateField) {
        jQuery(fromDateField).datetimepicker({
            ...datePickerOptions,
            onSelectDate: function(ct, $i) {
                const nextDay = new Date(ct);
                nextDay.setDate(nextDay.getDate() + 1);

                jQuery(toDateField).datetimepicker('setOptions', {
                    minDate: nextDay,
                    yearStart: ct.getFullYear(),
                    monthStart: ct.getMonth(),
                });
            }
        });
    }

    if (toDateField) {
        jQuery(toDateField).datetimepicker({
            ...datePickerOptions,
        });
    }
}
